import { useCallback, useSyncExternalStore } from 'react';

import { featureFlagClient } from './core/featureFlagClient';
import type { FeatureFlagProvider } from './core/featureFlagClient.types';
import type { PortalFeatureFlagDefinition } from './featureFlagDefinition';
import {
  getFeatureFlagBooleanDetails,
  isBooleanFeatureFlagExplicitlyOff,
} from './getFeatureFlagBooleanDetails';

type BooleanPortalFeatureFlagDefinition = PortalFeatureFlagDefinition<
  FeatureFlagProvider,
  'boolean'
>;

/**
 * True only when the provider is ready and Split returned an explicit off/false
 * treatment. Missing flags (FLAG_NOT_FOUND / control) are not treated as off.
 */
export function useIsBooleanFeatureFlagExplicitlyOff(
  definition: BooleanPortalFeatureFlagDefinition,
  defaultValue = false
): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      featureFlagClient.onChange(onStoreChange, { provider: definition.provider }),
    [definition.provider]
  );
  const getSnapshot = useCallback((): boolean => {
    const details = getFeatureFlagBooleanDetails(definition, defaultValue);
    const isReady = featureFlagClient.isReady({ provider: definition.provider });
    return isBooleanFeatureFlagExplicitlyOff(details, isReady);
  }, [defaultValue, definition]);
  const getServerSnapshot = useCallback(() => false, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
